import styled, { css } from 'styled-components';

import {
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';
import { Tooltip } from '@telialt/common-tooltip/src/Tooltip';

import { TechnicalCharacteristicsAlignment } from './model';

type AlignmentVariant = { $alignment: TechnicalCharacteristicsAlignment };

export const Container = styled.dl<AlignmentVariant>`
    list-style: none;
    display: grid;
    ${({ $alignment = 'automatic' }) => {
        const twoColumns = css`
            grid-template-columns: 1fr 1fr;
            margin: 2.5rem 0;
        `;
        const threeColumns = css`
            grid-template-columns: 1fr 1fr 2fr;
            margin: ${getPurpurSpacing('800')} 0;
        `;

        return (
            ($alignment === '2-columns' && twoColumns) ||
            ($alignment === '3-columns' && threeColumns) ||
            [purpurMedia('sm')`${twoColumns}`, purpurMedia('lg')`${threeColumns}`].flat()
        );
    }};

    > dt:nth-of-type(2n - 1),
    > dd:nth-of-type(2n - 1) {
        background-color: ${getPurpurColor('gray50')};
    }
`;

export const TitleColumn = styled.h2<AlignmentVariant & { $rows: number }>`
    margin: 0 ${getPurpurSpacing('300')} 0 0;
    line-height: 1;

    ${({ $alignment = 'automatic', $rows }) => {
        const twoColumns = css`
            grid-column: 1 / span 2;
            grid-row: 1;
            margin-bottom: ${getPurpurSpacing('200')};
            font-size: ${getPurpurTypography('scale200')};
        `;
        const threeColumns = css`
            grid-column: 1;
            grid-row: 1 / span ${$rows};
            margin-bottom: 0;
            font-size: ${getPurpurTypography('scale400')};
        `;

        return (
            ($alignment === '2-columns' && twoColumns) ||
            ($alignment === '3-columns' && threeColumns) ||
            [purpurMedia('sm')`${twoColumns}`, purpurMedia('lg')`${threeColumns}`].flat()
        );
    }};
`;

export const KeyColumn = styled.dt<AlignmentVariant>`
    margin: unset;
    display: flex;
    padding-top: ${getPurpurSpacing('200')};
    padding-right: 0;
    padding-bottom: ${getPurpurSpacing('200')};
    padding-left: ${getPurpurSpacing('300')};
    word-break: break-word;

    ${({ $alignment = 'automatic' }) => {
        const twoColumns = css`
            grid-column: 1;
        `;
        const threeColumns = css`
            grid-column: 2;
        `;

        return (
            ($alignment === '2-columns' && twoColumns) ||
            ($alignment === '3-columns' && threeColumns) ||
            [purpurMedia('sm')`${twoColumns}`, purpurMedia('lg')`${threeColumns}`].flat()
        );
    }}
`;

export const ValueColumn = styled.dd<AlignmentVariant>`
    margin: unset;
    padding: ${getPurpurSpacing('200')} ${getPurpurSpacing('300')};
    font-weight: 700;
    word-break: break-word;

    ${({ $alignment = 'automatic' }) => {
        const twoColumns = css`
            grid-column: 2;
        `;
        const threeColumns = css`
            grid-column: 3;
        `;

        return (
            ($alignment === '2-columns' && twoColumns) ||
            ($alignment === '3-columns' && threeColumns) ||
            [purpurMedia('sm')`${twoColumns}`, purpurMedia('lg')`${threeColumns}`].flat()
        );
    }}
`;

export const TooltipPointer = styled(Tooltip)`
    height: ${getPurpurSpacing('300')};
    display: flex;
    align-items: center;
    margin-left: ${getPurpurSpacing('100')};
`;
